import { useEffect } from "react";

const MOBILE_QUERY = "(max-width: 767px)";
// Do not use the window "scroll" event here. Lighthouse and browser scroll
// restoration can emit it without a real user gesture and prematurely release
// every deferred image. A genuine mobile scroll starts with touch/pointer input.
const INTERACTION_EVENTS = ["pointerdown", "touchstart", "keydown"];
const subscribers = new Set();

let interactionDetected = false;
let listenersInstalled = false;

export function isJpegOrPng(url = "") {
  return /\.(?:jpe?g|png)(?:[?#]|$)/i.test(String(url));
}

export function isMobileViewport() {
  return typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches;
}

function removeInteractionListeners() {
  if (typeof window === "undefined" || !listenersInstalled) return;

  INTERACTION_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, notifyInteraction);
  });
  listenersInstalled = false;
}

function notifyInteraction() {
  if (interactionDetected) return;

  interactionDetected = true;
  removeInteractionListeners();
  subscribers.forEach((subscriber) => subscriber());
  subscribers.clear();
}

function installInteractionListeners() {
  if (
    typeof window === "undefined" ||
    listenersInstalled ||
    interactionDetected ||
    !isMobileViewport()
  ) {
    return;
  }

  INTERACTION_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, notifyInteraction, {
      once: true,
      passive: true,
    });
  });
  listenersInstalled = true;
}

export function subscribeToMobileImageInteraction(callback) {
  if (!isMobileViewport() || interactionDetected) {
    callback();
    return () => {};
  }

  subscribers.add(callback);
  installInteractionListeners();

  return () => subscribers.delete(callback);
}

// Images are no longer gated behind user interaction (crawlers such as
// Googlebot never interact, so gated images were never indexed). Images now
// always get a real src and rely on native loading="lazy" + decoding="async".
// subscribeToMobileImageInteraction above is still used to defer slider JS.
export function shouldDeferMobileImage() {
  return false;
}

export function useDeferredMobileImageSource(src) {
  return {
    src,
    deferred: false,
  };
}

// Kept under its old name for existing callers: adds native lazy loading and
// async decoding to CMS <img> tags (src/srcset are left untouched).
export function deferMobileJpegPngInHtml(html = "") {
  if (!html || typeof html !== "string") {
    return html;
  }

  return html.replace(/<img\b[^>]*>/gi, (imageTag) => {
    let optimizedTag = imageTag;

    if (!/\sloading\s*=/i.test(optimizedTag)) {
      optimizedTag = optimizedTag.replace(/^<img/i, '<img loading="lazy"');
    }
    if (!/\sdecoding\s*=/i.test(optimizedTag)) {
      optimizedTag = optimizedTag.replace(/^<img/i, '<img decoding="async"');
    }

    return optimizedTag;
  });
}

// Restores images that were rendered with the old data-mobile-src markup.
export function hydrateDeferredHtmlImages(root) {
  if (!root) return;

  root.querySelectorAll("img[data-mobile-src]").forEach((image) => {
    const srcset = image.getAttribute("data-mobile-srcset");
    const src = image.getAttribute("data-mobile-src");

    if (srcset) image.setAttribute("srcset", srcset);
    if (src) image.setAttribute("src", src);

    image.removeAttribute("data-mobile-srcset");
    image.removeAttribute("data-mobile-src");
    image.removeAttribute("data-mobile-deferred-image");
  });
}

export function useDeferredHtmlImages(containerRef, dependency) {
  useEffect(() => {
    hydrateDeferredHtmlImages(containerRef.current);
  }, [containerRef, dependency]);
}
